import { Injectable, OnInit, OnDestroy, NgZone, isDevMode } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { Subscription } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { StorageManagerService } from './storage-manager.service';
import { Utilities } from './utilities';
import { SettingsService } from './settings.service';

@Injectable()
export class AuthenticationService implements OnInit, OnDestroy {

    isAuthorized: boolean;
    logoutStarted = false;
    subscription: Subscription;
    private applicationName = 'SSDrugProducts';

    constructor(private oidcSecurityService: OidcSecurityService, private http: HttpClient,
       private storageManagerService: StorageManagerService, private ngzone: NgZone, private settingsService: SettingsService) {
        
    }

    ngOnInit() {
        this.subscription = this.oidcSecurityService.isAuthenticated$.subscribe(
            (result) => {
                this.isAuthorized = result.isAuthenticated;
            });
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    getIsAuthorized(): Observable<boolean> {
        return this.oidcSecurityService.getAccessToken().pipe(map(token=>{ return token !== '';}));
    }

    getUserData(): Observable<boolean> {
        return this.oidcSecurityService.userData$.pipe(
            map(userData => !!userData)
        );
    }

    login() {
        console.log('start login');
        this.oidcSecurityService.authorize();
    }

    loginEntraId() {
        this.oidcSecurityService.authorize("identityServerEsig", { customParams: { prompt: 'login' }});
    }

    logout() {
      console.log('start logoff');
      this.logoutStarted = true;
      this.oidcSecurityService.getConfiguration('identityServer').subscribe((result) => {
        if (result?.authWellknownEndpoints?.endSessionEndpoint) {
            const logoutRedirect = result.authWellknownEndpoints?.endSessionEndpoint;
            if (logoutRedirect) {
                this.oidcSecurityService.logoffAndRevokeTokens('identityServer').subscribe({
                    next: () => this.completeLogout(logoutRedirect),
                    error: (error) => {
                        console.error('logoff failed', error);
                        this.completeLogout(logoutRedirect);
                    }
                });
            }
        } else {
            this.completeLogout();
        }
      });
    }

    private completeLogout(logoutRedirect?: string) {
      this.storageManagerService.clearAll();

      if (logoutRedirect) {
        window.location.href = logoutRedirect;
      }
    }

    // Logout
    public captureLogoutEvent(displayableId: string) {
        return this.get(`${window.location.origin}/api/user/logout?loginName=${displayableId}&appName=${this.applicationName}`);
    }

    getBearerToken(): string {
        let token =''; 
        this.storageManagerService.read('authzData') ? token = this.storageManagerService.read('authzData') : null;
        if (token === '') { return ''; }

        return 'Bearer ' + token;
    }

    get(url: string): Observable<any> {

        return this.http.get<any>(url, { headers: this.getHeaders() });
    }

    post(url: string, data: any): Observable<any> {

        const body = JSON.stringify(data);
        return this.http.post<any>(url, body, { headers: this.getHeaders() });
    }

    private getHeaders(permission?: string, setContentType?: boolean) {

        const authData = this.getAuthData();

        const customer = this.storageManagerService.read('currentCustomer');

        if (customer && !Utilities.TestIsObjectEmpty(customer)) {

            if (typeof customer !== 'object') {

                authData['customerId'] = JSON.parse(customer).customerId.toString();
            } else {

                authData['customerId'] = customer.customerId.toString();
            }
        } else {
            authData['customerId'] = '';
        }

        let headers = new HttpHeaders(
            {
                'UserId': authData.userGuid,
                'CustomerId': authData.customerId ? authData.customerId : '',
                'ApplicationVariantId':  authData.applicationVariantId ? authData.applicationVariantId.toString() : '',
                'Permission': permission ? permission : ''
            });

        if (setContentType) {
            headers = headers.set('Content-Type', 'application/json');
        }

        return this.appendAuthHeader(headers);
    }

    private appendAuthHeader(headers: HttpHeaders) {
      const bearerToken = this.getBearerToken();

      if (bearerToken === '') { return headers; }

      headers = headers.set('Authorization', bearerToken);

      return headers;
    }

    public getAuthData(): any {

        let applications: [] = [];
      let appVariantId: string;
      let appVariantName: string;
        let authData = {};

        authData = this.storageManagerService.read('userData');

        if (authData) {

            const userGuid = authData['userguid'];

            if (authData['applications']) {
                applications = JSON.parse(authData['applications']);

              if (applications.length > 0) {
                    // CAUTION: Following logic will break if we have multiple application variants for the same application name.
                    appVariantId = applications.find(a => a['AName'] === this.applicationName)['AVId'];
                    appVariantName = applications.find(a => a['AName'] === this.applicationName)['AVName'];

                    if (userGuid && appVariantId) {

                        authData['userGuid'] = userGuid;
                        authData['customerId'] = '';
                        authData['applicationVariantId'] = appVariantId.toString();
                        authData['applicationName'] = this.applicationName;
                        authData['applicationVariantName'] = appVariantName;
                    }
                }
            }
        }

        return authData;
    }

    public setHeader(permission?: any, setContentType?: boolean): HttpHeaders {

        return this.getHeaders(permission, setContentType);
    }

    eSigConfirmation(paramsContainer: any) {

      const settings = this.settingsService.getSettings();
      const action = `ids/v1/User/ESignature`;
      const headers = this.setHeader();

      return this.http.post(`${settings.CommonApiGatewayUrl}/${action}`, paramsContainer, { headers: headers })
        .pipe(map(this.extractData),
          catchError(this.handleErrors));
    }

    initPendo(userguid, customerName) {
        this.ngzone.runOutsideAngular(function () {
            (function (apiKey) {
                (function (p, e, n, d, o) {

                    let v, w, x, y, z; o = p[d] = p[d] || {}; o._q = [];

                    v = ['initialize', 'identify', 'updateOptions', 'pageLoad'];

                    for (w = 0, x = v.length; w < x; ++w) {

                        (function (m) {
                            o[m] = o[m] || function () { o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0))); };
                        })(v[w]);
                    }

                    y = e.createElement(n); y.async = !0; y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
                    z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
                })(window, document, 'script', 'pendo');

                // Please use Strings, Numbers, or Bools for value types.
                window['pendo'].initialize({
                    visitor: {
                        id: userguid  // Required if user is logged in
                    },

                    account: {
                        id: customerName

                        // You can add any additional account level key-values here,
                        // as long as it's not one of the above reserved names.
                    }
                });
            })('db0311d4-d596-4d56-6297-bfb0b812a06a');
        });
    }

    private extractData(response: Response) {
      if (response.status < 200 || response.status >= 300) {
        throw new Error('Bad response status: ' + response.status);
      }

      const body = response;
      return body || {};
    }

    // Error handling
    private handleErrors(error: any): Observable<any> {
      const GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';
      const bDisplayDetailMsg = error && error.error && error.error.isBusinessErrorMessage ? true : isDevMode() ? true : false;
      error.error = error && error.error && error.error.message && bDisplayDetailMsg ? error.error.message : GENERIC_ERROR_MSG;

      return throwError(error.error);
    }
}
